import { isNil, minBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Affected from 'pages/Bulletin/components/Affected';
import AiInsights from 'pages/Bulletin/components/AiInsights';
import ContentLayout from 'pages/Bulletin/components/ContentLayout';
import DataTable from 'pages/Bulletin/components/DataTable';
import Detections from 'pages/Bulletin/components/Detections';
import Metrics from 'pages/Bulletin/components/Metrics';
import NavBar from 'pages/Bulletin/components/NavBar';
import NavigationItem from 'pages/Bulletin/components/NavigationItem';
import RevisionSelect from 'pages/Bulletin/components/RevisionSelect';
import Social from 'pages/Bulletin/components/Social';
import SourceData from 'pages/Bulletin/components/SourceData';
import Summary from 'pages/Bulletin/components/Summary';
import Tags from 'pages/Bulletin/components/Tags';
import { Navigation } from 'pages/Bulletin/constants';
import usePackagesTableData from 'pages/Bulletin/hooks/use-packages-table-data';
import usePathsTableData from 'pages/Bulletin/hooks/use-paths-table-data';
import useReferencesTableData from 'pages/Bulletin/hooks/use-references-table-data';
import useRelatedTableData from 'pages/Bulletin/hooks/use-related-table-data';
import useSocialMediaPosts from 'pages/Bulletin/hooks/use-social-media-posts';
import { BulletinData } from 'pages/Bulletin/types/bulletin-data';
import animate from 'utils/animate';
import isEmptyRecursive from 'utils/is-empty-recursive';

import useStyles from './styles';

const COLLAPSE_THRESHOLD = 60;
const SCROLL_DURATION = 1000;

interface Props {
    data: BulletinData;
    isMobile: boolean;
}

const BulletinDetails: React.FC<Props> = ({ data, isMobile }) => {
    const { classes } = useStyles();

    const related = useRelatedTableData(data.related);
    const references = useReferencesTableData(data.references);
    const packages = usePackagesTableData(data.packages);
    const paths = usePathsTableData(data.webVulnerabilities);
    const posts = useSocialMediaPosts(data.social);

    const mutexRef = useRef(false);
    const layoutRef = useRef<HTMLDivElement | null>(null);
    const navbarRef = useRef<HTMLDivElement | null>(null);

    const navigationItems = useMemo(
        () =>
            [
                isEmptyRecursive(related.data) ? null : Navigation.RELATED,
                isEmptyRecursive(data.detections) ? null : Navigation.DETECTIONS,
                isEmptyRecursive(data.affected) ? null : Navigation.AFFECTED,
                isEmptyRecursive(packages.data) ? null : Navigation.PACKAGES,
                isEmptyRecursive(references.data) ? null : Navigation.REFERENCES,
                isEmptyRecursive(paths.data) ? null : Navigation.PATHS,
                isEmptyRecursive(posts) ? null : Navigation.SOCIAL,
                isEmptyRecursive(data.sourceData) ? null : Navigation.CODE
            ].filter((item): item is Navigation => !!item),
        [
            data.affected,
            data.detections,
            data.sourceData,
            packages.data,
            paths.data,
            posts,
            references.data,
            related.data
        ]
    );

    const [collapsed, setCollapsed] = useState(false);
    const [navigation, setNavigation] = useState<Navigation>(navigationItems[0]);

    useEffect(() => setNavigation((item) => item || navigationItems[0]), [navigationItems]);

    const scrollTo = useCallback((value: Navigation) => {
        const element = document.querySelector(`[data-navigation=${value}]`);
        const scrollable = layoutRef.current;
        const navbar = navbarRef.current;

        if (element && scrollable && navbar) {
            setCollapsed(true);
            setNavigation(value);
            const delta =
                element.getBoundingClientRect().top -
                scrollable.getBoundingClientRect().top -
                navbar.getBoundingClientRect().height;
            const from = scrollable.scrollTop;

            mutexRef.current = true;

            window.setTimeout(() => {
                animate({
                    duration: SCROLL_DURATION,
                    easing: animate.easing.easeInOutCubic,
                    onProgress: (progress) => scrollable.scrollTo(0, from + progress * delta)
                }).then(() => {
                    mutexRef.current = false;
                });
            }, 0);
        }
    }, []);

    const onScroll = useCallback(() => {
        const scrollable = layoutRef.current;
        const navbar = navbarRef.current;

        if (mutexRef.current || isNil(navbar) || isNil(scrollable)) {
            return;
        }

        window.requestAnimationFrame(() => {
            const navbarRect = navbar.getBoundingClientRect();
            const wrapperRect = scrollable.getBoundingClientRect();
            const items = document.querySelectorAll(`[data-navigation]`);

            const element = minBy([...items], (item) => {
                const { top, bottom } = item.getBoundingClientRect();
                return Math.min(Math.abs(top - navbarRect.bottom), Math.abs(bottom - navbarRect.bottom));
            });

            if (element instanceof HTMLElement) {
                setNavigation(element.dataset.navigation as Navigation);
            }

            setCollapsed(navbarRect.top - wrapperRect.top < COLLAPSE_THRESHOLD);
            mutexRef.current = false;
        });

        mutexRef.current = true;
    }, []);

    return (
        <ContentLayout outerRef={layoutRef} onOuterScroll={onScroll}>
            <Summary {...data} className={classes.summary} />
            <AiInsights id={data.id} />
            {isMobile ? (
                <div className={classes.section}>
                    <Tags data={data.raw.data} compact className={classes.tags} />
                    <RevisionSelect id={data.id} published={data.published} />
                    <Metrics metrics={data.metrics} epss={data.epss} aiScore={data.aiScore} />
                </div>
            ) : null}
            <NavBar
                ref={navbarRef}
                className={classes.navbar}
                value={navigation}
                values={navigationItems}
                onChange={scrollTo}
                collapsed={collapsed}
            />
            <div className={classes.items}>
                <NavigationItem value={Navigation.RELATED} hidden={!navigationItems.includes(Navigation.RELATED)}>
                    <DataTable {...related} />
                </NavigationItem>
                <NavigationItem value={Navigation.DETECTIONS} hidden={!navigationItems.includes(Navigation.DETECTIONS)}>
                    <Detections detections={data.detections} />
                </NavigationItem>
                <NavigationItem value={Navigation.AFFECTED} hidden={!navigationItems.includes(Navigation.AFFECTED)}>
                    <Affected affected={data.affected} />
                </NavigationItem>
                <NavigationItem value={Navigation.PACKAGES} hidden={!navigationItems.includes(Navigation.PACKAGES)}>
                    <DataTable {...packages} />
                </NavigationItem>
                <NavigationItem value={Navigation.REFERENCES} hidden={!navigationItems.includes(Navigation.REFERENCES)}>
                    <DataTable {...references} />
                </NavigationItem>
                <NavigationItem value={Navigation.PATHS} hidden={!navigationItems.includes(Navigation.PATHS)}>
                    <DataTable {...paths} />
                </NavigationItem>
                <NavigationItem value={Navigation.SOCIAL} hidden={!navigationItems.includes(Navigation.SOCIAL)}>
                    <Social data={posts} />
                </NavigationItem>
                <NavigationItem value={Navigation.CODE} hidden={!navigationItems.includes(Navigation.CODE)}>
                    <SourceData sourceData={data.sourceData} />
                </NavigationItem>
            </div>
        </ContentLayout>
    );
};

export default BulletinDetails;
